import React, { useEffect, useId, useState } from 'react';
import { MacrosPanel } from './components/MacrosPanel';
import { SufficiencyChart } from './components/SufficiencyChart';
import { SufficiencyHeader } from './components/SufficiencyHeader';
import { SufficiencySummaryTiles } from './components/SufficiencySummaryTiles';
import { COPY } from './copy';
import { useSufficiencyTrend } from './hooks/useSufficiencyTrend';
import { todayIst } from './lib/dates';
import { chartSummary } from './lib/sufficiency';
import type { DateRange, Granularity } from './types';
import './NutritionSufficiency.scss';

function CardSkeleton() {
  return (
    <div className="ns-in" aria-hidden>
      <div className="ns-kpis">
        {[0, 1, 2].map((k) => (
          <div className="ns-kpi" key={k}>
            <div className="ns-sk ns-sk-label" />
            <div className="ns-sk ns-sk-value" />
          </div>
        ))}
      </div>
      <div className="ns-panel">
        <div className="ns-sk ns-sk-title" />
        <div className="ns-sk ns-sk-chart" />
      </div>
      <div className="ns-panel ns-side">
        <div className="ns-sk ns-sk-title" />
        {[0, 1, 2].map((k) => (
          <div className="ns-sk ns-sk-mrow" key={k} />
        ))}
      </div>
    </div>
  );
}

export interface NutritionSufficiencyCardProps {
  /** Coach portal: the patient on screen. Omit in the app to read the logged-in patient. */
  patientId?: string;
  initialGranularity?: Granularity;
}

export function NutritionSufficiencyCard({ patientId, initialGranularity = 'daily' }: NutritionSufficiencyCardProps) {
  const titleId = useId();
  const [granularity, setGranularity] = useState<Granularity>(initialGranularity);
  // null: let the server apply its default span for the granularity. A picked
  // range is kept across granularity switches, so only the bars regroup.
  const [customRange, setCustomRange] = useState<DateRange | null>(null);
  const [page, setPage] = useState(1);
  const rangeFrom = customRange?.from;
  const rangeTo = customRange?.to;

  useEffect(() => {
    setPage(1);
  }, [granularity, rangeFrom, rangeTo]);

  const { data, loading, error, refetch } = useSufficiencyTrend({
    patientId,
    granularity,
    from: rangeFrom,
    to: rangeTo,
    page,
  });

  useEffect(() => {
    if (data?.pagination && page > data.pagination.total_pages) {
      setPage(1);
    }
  }, [data?.pagination, page]);

  const shownRange =
    customRange ??
    (data?.pagination ? { from: data.pagination.requested_from, to: data.pagination.requested_to } : null) ??
    (data ? { from: data.from, to: data.to } : null);
  const noPlan = !!data && !data.plan_window && data.summary.days_planned === 0;

  let body: React.ReactNode;
  if (error) {
    body = (
      <div className="ns-state" role="alert">
        <p>{error}</p>
        <button type="button" className="ns-retry" onClick={refetch}>
          Try again
        </button>
      </div>
    );
  } else if (!data) {
    body = <CardSkeleton />;
  } else if (noPlan) {
    body = (
      <div className="ns-state">
        <p>{COPY.noPlan}</p>
        <span>{COPY.noPlanHint}</span>
      </div>
    );
  } else {
    const { summary } = data;
    body = (
      <div className="ns-in">
        <SufficiencySummaryTiles summary={summary} medalScore={data.medal_score} />
        <SufficiencyChart
          granularity={data.granularity}
          buckets={data.buckets}
          averageScore={summary.average_score}
          medalScore={data.medal_score}
          summary={chartSummary(summary.average_score, summary.medal_days, summary.days_logged)}
          busy={loading}
          pagination={
            data.pagination && data.pagination.total_pages > 1
              ? { page: data.pagination.page, totalPages: data.pagination.total_pages, onPageChange: setPage }
              : undefined
          }
        />
        <MacrosPanel macros={data.macros} days={data.days} daysLogged={summary.days_logged} />
      </div>
    );
  }

  return (
    <section className="ns" aria-labelledby={titleId} aria-busy={loading}>
      <SufficiencyHeader
        titleId={titleId}
        granularity={granularity}
        onGranularityChange={setGranularity}
        range={shownRange}
        onRangeChange={setCustomRange}
        today={data?.today ?? todayIst()}
        planWindow={data?.plan_window ?? null}
      />
      {body}
    </section>
  );
}

export default NutritionSufficiencyCard;
